import React from 'react';
import { useTranslation } from 'react-i18next';
import type { NorthStarPayload } from '../types';
import { Icon } from './shared/Icon';
import {
  formatComponentValue,
  formatNorthStarValue,
} from '../lib/northStarFormat';

function NorthStarMain({
  payload,
  improving,
  value,
  suffix,
  max,
  baselineDelta,
}: {
  payload: NorthStarPayload;
  improving: boolean;
  value: string;
  suffix: string;
  max: number;
  baselineDelta: number;
}) {
  const { t } = useTranslation('performance');
  return (
    <>
      <div className="ns-hero-title">{payload.label}</div>
      {payload.description && (
        <p className="ns-hero-desc">{payload.description}</p>
      )}
      <div className="ns-hero-stats">
        <div className="ns-hero-value">
          {value}
          {suffix && <span className="ns-hero-unit">{suffix}</span>}
        </div>
        <div className={`ns-hero-delta${improving ? ' up' : ' down'}`}>
          <Icon name={improving ? 'arrowUp' : 'arrowDown'} size={13} />
          {t('northStar.vsBaseline', { pct: Math.abs(baselineDelta) })}
        </div>
        {payload.series.length > 1 && (
          <div className="ns-hero-spark" aria-hidden>
            {payload.series.map((v, i) => (
              <span
                key={i}
                className="ns-hero-spark-bar"
                style={{ height: `${Math.max(12, (v / max) * 100)}%` }}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export function NorthStarHero({
  payload,
  onDrill,
  onEdit,
}: {
  payload: NorthStarPayload;
  onDrill?: () => void;
  onEdit?: () => void;
}) {
  const baselineDelta =
    payload.baseline.value !== 0
      ? +(
          ((payload.currentValue - payload.baseline.value) /
            Math.abs(payload.baseline.value)) *
          100
        ).toFixed(1)
      : payload.trendPct;
  const improving = baselineDelta >= 0;
  const { value, suffix } = formatNorthStarValue(
    payload.currentValue,
    payload.spec.unit,
  );
  const max = Math.max(...payload.series, 1);
  const { t } = useTranslation('performance');

  return (
    <section className="ns-hero" aria-label={t('northStar.aria')} data-ftue="north-star">
      <div className="ns-hero-head">
        <span className="ns-hero-badge" aria-hidden>
          <Icon name="star" size={16} />
        </span>
        <div className="ns-hero-head-copy">
          <div className="ns-hero-kicker">{t('northStar.kicker')}</div>
        </div>
        {(onEdit || onDrill) && (
          <div className="ns-hero-actions">
            {onEdit ? (
              <button type="button" className="tb-btn" onClick={onEdit}>
                <Icon name="sliders" size={14} />
                {t('northStar.edit')}
              </button>
            ) : null}
            {onDrill ? (
              <button type="button" className="tb-btn accent" onClick={onDrill}>
                {t('northStar.drillDown')}
              </button>
            ) : null}
          </div>
        )}
      </div>
      {onDrill ? (
        <button type="button" className="ns-hero-main" onClick={onDrill}>
          <NorthStarMain payload={payload} improving={improving} value={value} suffix={suffix} max={max} baselineDelta={baselineDelta} />
        </button>
      ) : (
        <div className="ns-hero-main">
          <NorthStarMain payload={payload} improving={improving} value={value} suffix={suffix} max={max} baselineDelta={baselineDelta} />
        </div>
      )}
      {payload.componentBreakdown.length > 0 && (
        <div className="ns-hero-components">
          {payload.componentBreakdown.map(c => (
            onDrill ? (
              <button
                key={c.metricKey}
                type="button"
                className="ns-comp-chip"
                onClick={onDrill}
              >
                <span className="ns-comp-label">{c.label}</span>
                <span className="ns-comp-value">
                  {formatComponentValue(c.value, c.unit)}
                </span>
              </button>
            ) : (
              <div key={c.metricKey} className="ns-comp-chip">
                <span className="ns-comp-label">{c.label}</span>
                <span className="ns-comp-value">
                  {formatComponentValue(c.value, c.unit)}
                </span>
              </div>
            )
          ))}
        </div>
      )}
    </section>
  );
}
